import { Alert, AlertDescription, Badge, Icon, Text, Tabs, TabsList, TabsTrigger, IconName } from '@flatfile/baseline'
import { useState, useEffect } from 'react'
import { useCollection } from '../resources/hooks/useCollection'
import getCollection from '../resources/support/getCollection'
import { AppCTAs } from '../components/Apps/AppCTAs'
import CreateAppPath from '../components/Apps/CreateAppModalPath'
import { useActiveEnvironment } from '../hooks/useEnvironments'
import { useDeleteResource } from '../resources/hooks/useResourceMutation'
import { useIdentity } from '../hooks/useIdentity'
import { useEntitlements } from '../hooks/useEntitlements'
import { CustomErrorComponent } from '../components/Shared/CustomErrorComponent'
import { logger } from '../utils/logging'
import { DeleteAppModal } from './DeleteAppModal'
import { renderMappedIcon } from '../components/Shared/IconMapper'
import { AppCardsDisplay } from '../components/Apps/AppCardsDisplay'
import TrialBanner from '../components/Shared/TrialBanner'
import { App } from '@flatfile/api-internal/api'

export function HomePage() {
  const { environment } = useActiveEnvironment()
  const { hasPermission, identity } = useIdentity()
  const apps = useCollection('APPS')
  const [filteredApps, setFilteredApps] = useState<App[]>([])
  const { hasEntitlement } = useEntitlements()
  const canDeleteApps = hasPermission({ resource: identity.account, permissionKey: 'app:delete' })
  const draftApps = filteredApps.filter((app) => !app.activatedAt)
  const activatedApps = filteredApps.filter((app) => app.activatedAt)
  const [activeTab, setActiveTab] = useState('all')

  useEffect(() => {
    const filtered = apps.filter((app) =>
      environment?.isProd
        ? app.environmentFilters.type?.includes('PROD') ?? false
        : app.environmentFilters.type?.includes('DEV') ?? false
    )
    setFilteredApps(filtered)
  }, [environment, apps])

  const displayedApps = () => {
    switch (activeTab) {
      case 'live':
        return activatedApps
      case 'drafts':
        return draftApps
      default:
        return filteredApps
    }
  }

  const renderEmptyFilterState = () => {
    const currentApps = displayedApps()
    if (filteredApps.length > 0 && currentApps.length === 0) {
      const message =
        activeTab === 'live'
          ? 'No live apps found. Activate a draft app to see it here.'
          : 'No draft apps found. Create a new app to get started.'

      return (
        <div className='col-span-3 flex flex-col items-center justify-center py-16 px-4 text-center'>
          <Icon name={activeTab === 'live' ? 'play' : 'edit-small-2'} className='mb-4 text-gray-400 size-8' />
          <Text variant='body' className='mb-2 text-gray-600'>
            {message}
          </Text>
          {activeTab === 'drafts' && hasPermission({ resource: identity.account, permissionKey: 'app:create' }) && (
            <div className='mt-4'>
              <CreateAppPath mini={false} />
            </div>
          )}
        </div>
      )
    }
    return null
  }

  return (
    <div className='!w-full !bg-base-white !min-h-[100vh]'>
      <div className='p-3 w-full h-full flex flex-col'>
        <div className='p-3 flex justify-between items-center'>
          <Text variant='title2'>Apps</Text>
          <div className='flex gap-2'>
            <TrialBanner />
            {hasPermission({ resource: identity.account, permissionKey: 'app:create' }) && (
              <CreateAppPath mini={false} />
            )}
          </div>
        </div>

        {filteredApps.length > 0 && (
          <>
            <Tabs
              defaultValue='all'
              value={activeTab}
              onValueChange={setActiveTab}
              className='border-b border-gray-200'
            >
              <div className='flex justify-between items-center px-2 w-full mt-3'>
                <TabsList className='bg-transparent p-0 h-full gap-1'>
                  <TabsTrigger
                    value='all'
                    className='bg-transparent px-4 py-2 min-h-[44px] data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:rounded-none hover:text-primary rounded-none'
                  >
                    <Text
                      variant='body'
                      emphasized={activeTab === 'all'}
                      className={activeTab === 'all' ? '' : 'text-muted'}
                    >
                      All
                    </Text>
                  </TabsTrigger>
                  <TabsTrigger
                    value='live'
                    className='flex items-center bg-transparent gap-2 px-4 py-2 min-h-[44px] data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:rounded-none hover:text-primary rounded-none'
                  >
                    <Icon name='play' size={16} />
                    <Text
                      variant='body'
                      emphasized={activeTab === 'live'}
                      className={activeTab === 'live' ? '' : 'text-muted'}
                    >
                      Live
                    </Text>
                  </TabsTrigger>
                  <TabsTrigger
                    value='drafts'
                    className='flex items-center bg-transparent gap-2 px-4 py-2 min-h-[44px] data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:rounded-none hover:text-primary rounded-none'
                  >
                    <Icon name='edit-small-2' size={16} />
                    <Text
                      variant='body'
                      emphasized={activeTab === 'drafts'}
                      className={activeTab === 'drafts' ? '' : 'text-muted'}
                    >
                      Drafts
                    </Text>
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          </>
        )}

        {apps.length > 0 && filteredApps.length === 0 && (
          <>
            <Alert className='min-h-[62px] mt-4' variant='outline'>
              <div className='flex items-center gap-2'>
                <Icon name='light-bulb' size={16} />
                <AlertDescription>
                  Note: There are Apps present in other environments. Swap to one of your{' '}
                  {environment?.isProd ? 'dev' : 'prod'} environments to view.
                </AlertDescription>
              </div>
            </Alert>
          </>
        )}

        <div className='px-2 py-4'>
          {filteredApps.length === 0 ? (
            hasEntitlement('portalEnabled') ||
            hasEntitlement('projectsEnabled') ||
            hasEntitlement('customAppsEnabled') ||
            hasEntitlement('autobuild') ? (
              <div className='w-full h-full flex flex-col'>
                <div className='w-full max-w-3xl mx-auto px-6 text-center py-12'>
                  <Text variant='title1' emphasized className='mb-4 block'>
                    How would you like to get started?
                  </Text>
                  <Text variant='body' className='py-2 max-w-[550px] mx-auto'>
                    Flatfile Apps streamline data handling from basic CSV imports to advanced enterprise tasks. Install
                    an App that fits your needs or create your own to begin tackling your data challenges.
                  </Text>
                </div>
                <div className='flex-1 overflow-y-auto'>
                  <div className='flex justify-center px-6 pb-4'>
                    <div className='overflow-x-auto max-w-full'>
                      <AppCardsDisplay />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <CustomErrorComponent
                page='home.page.tsx'
                message='There are no apps enabled on your account. Please reach out to support'
                doNotCenter
              ></CustomErrorComponent>
            )
          ) : (
            <div className='grid grid-cols-3 gap-4'>
              {displayedApps().length > 0
                ? displayedApps().map((app) => (
                    <div key={app.id}>
                      <UserAppCard app={app} canDelete={canDeleteApps} />
                    </div>
                  ))
                : renderEmptyFilterState()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// UserAppCard.tsx
const UserAppCard = ({ app, canDelete }: { app: App; canDelete: boolean }) => {
  const [openedDeleteApp, setOpenedDeleteApp] = useState(false)
  const openDeleteApp = () => setOpenedDeleteApp(true)
  const closeDeleteApp = () => setOpenedDeleteApp(false)
  const [deleteApp] = useDeleteResource('APPS')
  const isLive = app.activatedAt

  const handleDeleteClick = async (appId: string) => {
    try {
      await deleteApp(appId)
      getCollection('APPS').refresh()
      closeDeleteApp()
    } catch (error) {
      logger.error('Error deleting app', { error })
    }
  }

  return (
    <div className='border rounded-lg bg-base-white overflow-hidden flex flex-col h-full'>
      {openedDeleteApp && (
        <DeleteAppModal app={app} opened={openedDeleteApp} onClose={closeDeleteApp} onDelete={handleDeleteClick} />
      )}

      <div className='p-3 mb-4'>
        {canDelete && (
          <div className='flex justify-between mb-3'>
            <Badge variant={isLive ? 'default' : 'secondary'} radius='full'>
              {isLive ? 'Live' : 'Draft'}
            </Badge>
            <button
              type='button'
              className='bg-transparent text-muted hover:text-primary border-none cursor-pointer'
              onClick={openDeleteApp}
            >
              <Icon name='trash-can-2' size={16} />
            </button>
          </div>
        )}

        <div className='flex items-center gap-2'>
          {renderMappedIcon({
            iconName: app.icon as IconName,
            size: 18,
            namespace: app.namespace,
            className: 'text-primary',
          })}
          <Text variant='title3' emphasized className='truncate'>
            {app.name}
          </Text>
        </div>
      </div>

      <div className='border-t border-gray-200 w-full'></div>

      <div className='p-2 flex justify-end'>
        {app && (
          <AppCTAs
            appType={app.type}
            activatedAt={
              app.activatedAt
                ? typeof app.activatedAt === 'string'
                  ? app.activatedAt
                  : app.activatedAt.toISOString()
                : undefined
            }
            createdAt={typeof app.createdAt === 'string' ? app.createdAt : app.createdAt.toISOString()}
            customEntityPlural={app.entityPlural}
            appId={app.id}
          />
        )}
      </div>
    </div>
  )
}
